import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { uuid } from 'uuidv4';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import Section from '../../components/Section/Section';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import { variables } from '../../assets/styles/variables';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import NagDashboardBanner from '../../components/NagDashboard/NagDashboardBanner';
// import LegacyOrderHistorySearch from '../../components/LegacyOrderHistoryTable/LegacyOrderHistorySearch';
import LegacyOrderHistoryRow from '../../components/LegacyOrderHistoryTable/LegacyOrderHistoryRow';
import { fetchLegacyOrders } from '../../store/actions/legacyOrders/legacyOrderActions';
import {
  selectLegacyOrders,
  selectLegacyOrdersTotalCount,
} from '../../store/selectors/legacyOrdersSelector';
import SectionLoader from '../../components/Loader/SectionLoader';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { FETCH_LEGACY_ORDERS_LOADING } from '../../store/actions/legacyOrders/legacyOrderActionConstants';
import LoadMore from '../../components/LoadMore/LoadMore';
import { useLegacyOrderPagination } from '../../util/hooks/useLegacyOrderPagination';
// import {
//   ORDER_STATUS_TYPE_PICKER,
//   ORDER_TYPES,
//   ORDER_TYPES_LABELS,
// } from '../../util/helpers/legacyOrderHelper';
import NoResults from '../../components/NoResults/NoResults';
import { useBrokerName } from '../../util/hooks/userBrokerName';

const ManagementAddNewContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
  align-items: center;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    margin-bottom: ${pxToRemMd(24)};
  }
`;

const LegacyOrderHistoryContainer = styled.div`
    margin: ${pxToRem(32)} 0;
    display: grid;
    grid-template-columns: 1fr 2fr;
    grid-gap: ${pxToRem(32)};

    ${mediaBelow(1400)} {
      grid-template-columns: 1fr;
    }

${mediaBelow(variables.breakpoints.bpXl)} {
  margin: ${pxToRem(16)} 0;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    margin: ${pxToRem(24)} ${pxToRem(16)};

    ${ManagementAddNewContainer} {
      display: flex;

      form {
        display: flex;
      }
    }
  }
`;

const LegacyOrderHistoryDescription = styled.p`
  margin-bottom: ${pxToRem(32)};

  > :not(:first-child) {
    margin-top: ${pxToRem(56)};
  }

  ${mediaBelow(1400)} {
    margin-bottom: ${pxToRem(24)};

    > :not(:first-child) {
      margin-top: ${pxToRem(56)};
    }
  }
`;

const LegacyOrderTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const LegacyOrderHistoryTableContainer = styled.div`
  width: 100%;
`;

const LegacyOrderHistoryPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const accountUid = useSelector(getCurrentAccountUid);
  const legacyOrders = useSelector(selectLegacyOrders);
  const legacyOrdersTotalCount = useSelector(selectLegacyOrdersTotalCount);
  const [page, setPageNumber] = useState(1);
  const size = 1000; // eslint-disable-line
  const isLoading = useSelector(
    selectIsLoadingByActionType(FETCH_LEGACY_ORDERS_LOADING),
  );
  // const [searchValue, setSearchValue] = useState('');
  // const [selectedOrderType, setSelectedOrderType] = useState(null);
  // const [selectedOrderStateType, setSelectedOrderStateType] = useState(null);
  // const [selectedProductCode, setSelectedProductCode] = useState(null);
  const brokerName = useBrokerName();
  const element = document.getElementById('scrollTo')?.offsetTop;

  const handleLoadMore = () => {
    setPageNumber((old) => old + 1);
  };

  const handleFetchOrders = (page) => {
    dispatch(fetchLegacyOrders({ accountUid, page, size }));
  };

  useEffect(() => {
    handleFetchOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid]);

  const [legacyOrdersList] = useLegacyOrderPagination(
    legacyOrders,
    page,
    // searchValue,
    // selectedOrderType,
    // selectedOrderStateType,
    // selectedProductCode,
  );

  // const ORDER_TYPES_LIST = [
  //   { label: t('common.all'), value: '' },
  //   ...Object.values(ORDER_TYPES).map((orderType) => ({
  //     value: orderType,
  //     label: ORDER_TYPES_LABELS[orderType],
  //   })),
  // ];

  // const ORDER_STATUS_TYPE = [
  //   { label: t('common.all'), value: '' },
  //   ...Object.values(ORDER_STATUS_TYPE_PICKER).map((statusType) => ({
  //     value: statusType,
  //     label: statusType,
  //   })),
  // ];

  // const uniqueOrderItems = useMemo(() => {
  //   const orderItems = (legacyOrders ?? []).reduce(
  //     (acc, currLegacyOrder) => acc.concat(currLegacyOrder.OrderItems),
  //     [],
  //   );
  //   return orderItems?.filter(
  //     (orderItem, index, array) =>
  //       array.findIndex(
  //         (orderItem2) => orderItem2.ProductCode === orderItem.ProductCode,
  //       ) === index,
  //   );
  // }, [legacyOrders]);

  // const PRODUCT_LIST = [
  //   { label: t('common.all'), value: '' },
  //   ...uniqueOrderItems?.map((orderItem) => ({
  //     value: orderItem.ProductCode,
  //     label: orderItem.ProductCaption,
  //   })),
  // ];

  return (
    <div id="scrollTo">
      <SectionLoader isLoading={isLoading}>
        <NagDashboardBanner />
        <NavigationBar
          mainPage={t('header.navDropdownAccount')}
          subPage={t('header.accessLegacyOrderHistory')}
          hideCtaOnMobile
        />
        <Section>
          <LegacyOrderHistoryContainer>
            <div>
              <LegacyOrderHistoryDescription>
                {t('account.legacyOrderHistory.description', { brokerName })}
              </LegacyOrderHistoryDescription>
              {/* <LegacyOrderHistorySearch
                setSearchValue={setSearchValue}
                orderTypesList={ORDER_TYPES_LIST}
                orderStatusTypes={ORDER_STATUS_TYPE}
                productList={PRODUCT_LIST}
                setSelectedOrderType={setSelectedOrderType}
                setSelectedOrderStateType={setSelectedOrderStateType}
                setSelectedProductCode={setSelectedProductCode}
              /> */}
            </div>
            <LegacyOrderHistoryTableContainer>
              <LegacyOrderTable>
                {legacyOrdersList?.map((order) => (
                  <LegacyOrderHistoryRow legacyOrder={order} key={uuid()} />
                ))}
              </LegacyOrderTable>
              {legacyOrdersList.length > 0 && (
                <LoadMore
                  loadMoreDisabled={page * 10 >= legacyOrdersTotalCount}
                  onLoadClick={handleLoadMore}
                  scrollToElement={element}
                />
              )}
              {!legacyOrdersList.length && !isLoading && (
                <NoResults>{t('common.noResults')}</NoResults>
              )}
            </LegacyOrderHistoryTableContainer>
          </LegacyOrderHistoryContainer>
        </Section>
      </SectionLoader>
    </div>
  );
};

export default LegacyOrderHistoryPage;
